/**
 * lib-blueprint
 *
 * Adapptio library for processing blueprints.
 *
 * @package lib-blueprint
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { DesignContext } from "../../Context/DesignContext";
import { exportSchema } from "../../ExportImportSchema/ExportSchema";
import { ISchemaImportExport } from "../../ExportImportSchema/ExportTypes";
import {
	IBlueprintSchema, TBlueprintSchemaParentNode,
	TGetBlueprintSchemaDefault,
	TGetBlueprintSchemaModel
} from "../../Schema/IBlueprintSchema";
import {
	cloneModelNode,
	compileValidateAsNotSupported,
	createEmptySchema,
	createModelNode,
	destroyModelNode,
	validateAsNotSupported,
	assignParentToModelProps
} from "../../Schema/SchemaHelpers";
import { ISchemaConstBoolean, SchemaConstBoolean } from "../const/SchemaConstBoolean";
import { ISchemaConstObject, ISchemaConstObjectOptsProp, Prop, SchemaConstObject } from "../const/SchemaConstObject";
import { ISchemaBuilderOpts, SchemaBuilderBaseProps, TCompareSchemaBuilderProps, TSchemaBuilderBaseProps } from "./SchemaBuilderShared";
import { IModelNode } from "../../Schema/IModelNode";
import { TypeDescAny } from "../../Shared/ITypeDescriptor";
import { applyCodeArg } from "../../Context/CompileUtil";
import { ISchemaConstInteger, SchemaConstInteger } from "../const/SchemaConstInteger";
import { ISchemaConstArray, ISchemaConstArrayOpts, SchemaConstArray } from "../const/SchemaConstArray";
import { ISchemaConstAny, SchemaConstAny, SCHEMA_CONST_ANY_VALUE_TYPE } from "../const/SchemaConstAny";
import { ISchemaBuilderAny, SchemaBuilderAny, SCHEMA_BUILDER_ANY_VALUE_TYPE } from "./SchemaBuilderAny";

type TSchemaBuilderArrayPropsSchema = ISchemaConstObject<
	TSchemaBuilderBaseProps & {
		items: ISchemaConstObjectOptsProp<ISchemaBuilderAny>;
		default: ISchemaConstObjectOptsProp<ISchemaConstArray<ISchemaConstAny>>;
		fallbackValue: ISchemaConstObjectOptsProp<ISchemaConstArray<ISchemaConstAny>>;
		constraints: ISchemaConstObjectOptsProp<ISchemaConstObject<{
			required?: ISchemaConstObjectOptsProp<ISchemaConstBoolean>;
			minItems?: ISchemaConstObjectOptsProp<ISchemaConstInteger>;
			maxItems?: ISchemaConstObjectOptsProp<ISchemaConstInteger>;
		}>>
	}
>;

/**
 * Development helper to check types - when resolves to "true" then the props schema is correct and up to date.
 * Otherwise you have an error somewhere. It compares if SchemaBuilder...PropsSchema output (spec) equals to the interface
 * that describes options of a target schema the builder represents.
 */
type _TypeCheck = TCompareSchemaBuilderProps<
	ISchemaConstArrayOpts<ISchemaConstAny>,
	TSchemaBuilderArrayPropsSchema,
	"items"|"getElementModelNodeInfo"|"outlineOptions"
>;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const __HERE_SHOULD_BE_NO_TYPE_ERRORS__: _TypeCheck = true;

/**
 * Opts Schema
 */
export interface ISchemaBuilderArrayOptsSchema extends ISchemaBuilderOpts {}

/**
 * Schema Model
 */
export interface ISchemaBuilderArrayModel extends IModelNode<ISchemaBuilderArray> {
	props: TGetBlueprintSchemaModel<TSchemaBuilderArrayPropsSchema>
}

export type TSchemaBuilderArraySpec = ISchemaImportExport;
export type TSchemaBuilderArrayDefault = TGetBlueprintSchemaDefault<TSchemaBuilderArrayPropsSchema>;

/**
 * Schema Builder Boolean Schema
 */
export interface ISchemaBuilderArray extends IBlueprintSchema<
	ISchemaBuilderOpts,
	ISchemaBuilderArrayModel,
	ISchemaImportExport,
	TSchemaBuilderArrayDefault
> { }

/**
 * Schema Builder: Boolean
 *
 * @param opts Schema options
 */
export function SchemaBuilderArray(opts: ISchemaBuilderOpts): ISchemaBuilderArray {

	type TPropsModel = TGetBlueprintSchemaModel<TSchemaBuilderArrayPropsSchema>;

	const propsSchema: TSchemaBuilderArrayPropsSchema = SchemaConstObject({
		constraints: opts.constraints,
		props: {
			...SchemaBuilderBaseProps,
			items: Prop(SchemaBuilderAny({
				label: "Item",
				description: "Specification of a list item.",
				defaultType: SCHEMA_BUILDER_ANY_VALUE_TYPE.STRING,
				constantOnly: opts.constantOnly
			}), 50),
			default: Prop(SchemaConstArray({
				label: "Default value",
				description: "Default field value.",
				items: SchemaConstAny({
					defaultType: SCHEMA_CONST_ANY_VALUE_TYPE.STRING,
				})
			}), 60),
			fallbackValue: Prop(SchemaConstArray({
				label: "Fallback value",
				description: "Value which will be used when a field value is not valid.",
				items: SchemaConstAny({
					defaultType: SCHEMA_CONST_ANY_VALUE_TYPE.STRING
				})
			}), 70),
			constraints: Prop(SchemaConstObject({
				label: "Constraints",
				description: "Validation rules.",
				props: {
					required: Prop(SchemaConstBoolean({
						label: "Required",
						description: "If a value is required."
					}), 10),
					minItems: Prop(SchemaConstInteger({
						label: "Minimum items",
						description: "Number of a minimum required items in a list."
					}), 20),
					maxItems: Prop(SchemaConstInteger({
						label: "Maximum items",
						description: "Number of a maximum allowed items in a list."
					}), 30)
				}
			}), 80)
		}
	});

	const schema = createEmptySchema<ISchemaBuilderArray>("builderArray", opts);

	const assignParentToChildrenOf = (srcModel) => {
		return assignParentToModelProps(srcModel, ["props"])
	}

	const createModel = (
		dCtx: DesignContext,
		propsModel: TPropsModel, parent: TBlueprintSchemaParentNode
	) => {

		const modelNode = createModelNode(schema, dCtx, parent, [], {
			props: propsModel
		});

		const model = assignParentToChildrenOf(modelNode)

		model.initRequiredValid = propsModel.initRequiredValid;

		return model;

	};

	schema.createDefault = (dCtx, parent, defaultValue) => {

		const propsModel = propsSchema.createDefault(dCtx, null, defaultValue);
		return createModel(dCtx, propsModel, parent);

	};

	schema.clone = (dCtx, modelNode, parent) => {

		const clonedPropsModel = propsSchema.clone(dCtx, modelNode.props, null);

		const clone = cloneModelNode(dCtx, modelNode, parent,{
			props: clonedPropsModel
		})

		return assignParentToChildrenOf(clone)
	};

	schema.destroy = (modelNode) => {

		propsSchema.destroy(modelNode.props);
		modelNode.props = undefined;
		destroyModelNode(modelNode);

	};

	schema.parse = (dCtx, idtNode, parent) => {

		const propsModel = propsSchema.parse(dCtx, idtNode, null);
		return createModel(dCtx, propsModel, parent);

	};

	schema.provideCompletion = (dCtx, parentLoc, minColumn, idtNode) => {

		if (propsSchema.provideCompletion) {
			propsSchema.provideCompletion(dCtx, parentLoc, minColumn, idtNode);
		}

	};

	schema.serialize = (modelNode, path) => {

		return propsSchema.serialize(modelNode.props, path);

	};

	schema.render = (rCtx, modelNode, path, scope, prevSpec) => {

		const prevPropsSpec = prevSpec?._type === "SchemaValue" ? prevSpec._values[0]._values[0] : prevSpec?._values?.[0];
		const propsSpec = propsSchema.render(rCtx, modelNode.props, path, scope, prevPropsSpec);

		const exportData = {
			_type: "Schema",
			_name: "SchemaConstArray",
			_values: [
				propsSpec
			]
		};

		if (opts.constantOnly === true) {
			return exportData;
		} else {
			return {
				_type: "Schema",
				_name: "SchemaValue",
				_values: [exportData, propsSpec]
			};
		}

	};

	schema.compileRender = (cCtx, modelNode, path) => {

		const propsCmp = propsSchema.compileRender(cCtx, modelNode.props, path);

		return {
			isScoped: true,
			code: `(s,pv,pt)=>{${[
				// Previous props
				`const _pp=pv&&pv._type==="SchemaValue"?pv._values[0]._values[0]:pv&&pv._values?pv._values[0]:undefined;`,
				// opts
				`const _op=${applyCodeArg(propsCmp, `_pp`, `pt`)};`,
				// Export data
				`const _ed={_type:"Schema",_name:"SchemaConstArray",_values:[_op]};`,
				// Return
				opts.constantOnly
					? `return _ed`
					: `return {_type:"Schema",_name:"SchemaValue",_values:[_ed,_op]}`
			].join("")}}`
		}

	};

	/** Schema value cannot be validated because spec is ExportImport type which should not be constructed in any manuall way */
	schema.validate = (rCtx, path, modelNodeId) => {
		return validateAsNotSupported(rCtx, path, modelNodeId, schema.name);
	};

	schema.compileValidate = (cCtx, path, modelNodeId) => {
		return compileValidateAsNotSupported(cCtx, path, modelNodeId, schema.name);
	};

	schema.export = () => {
		return exportSchema("SchemaBuilderArray", [opts]);
	};

	/** Is any because export data are internals and should not be exposed. */
	schema.getTypeDescriptor = () => {
		return TypeDescAny({
			label: opts.label,
			description: opts.description,
			example: opts.example,
			tags: opts.tags
		});
	}

	schema.getChildNodes = (modelNode) => {
		return [{
			key: "props",
			node: modelNode.props
		}]
	}

	return schema;

}
